import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { FaRegQuestionCircle } from 'react-icons/fa';
import {
  cruiseSearchRequest,
  setCruiseSearchParam,
  hotelSearchRequest,
  setHotelTripSearchParam,
  setFlightTripSearchParam,
  setCarTripSearchParam,
  setTourTripSearchParam,
  tourSearchRequest,
} from '../../store/slices/tripSearch';

import {
  SearchStatus,
  CruiseSortValues,
  HotelSortingValues,
  FlightSortingValues,
  FiltersInitialValue,
  PageLinks,
  SearchModes,
  HotelSortingType,
  CustomItinerarySortingValues,
  CustomItinerarySortingType,
  SearchModeLabel,
  FlightSortingType,
  FlightSearchTypes,
  CarRentalSortingValues,
  TourSortingValues,
  TourFiltersInitialValue,
} from '../../constants';
import Pagination from '../Pagination';
import SortingFilter from '../Filters/SortingFilter';
import BrochureActions from '../TravelOption/BrochureActions';
import CompareActionsDropdown from '../Compare/CompareActionsDropdown';
import { flightSearch } from '../../store/slices/flight';
import { carSearch } from '../../store/slices/car';
import checkDoHaveMapMarkers from '../../helpers/selectors/checkDoHaveMapMarkers';
import ShowHideTripItinerariesMap from './ShowHideTripItinerariesMap';
import { setIsUserInteractedWithHotelList } from '../../store/slices/hotel';
import LoadMoreHotelsButton from '../TravelOption/HotelInformation/HotelPagination/LoadMoreHotelsButton';
import PricePreferenceFilter from '../TravelOption/HotelInformation/HotelFilterBar/PricePreferenceFilter';
import FightFilters from '../TravelOption/FlightInformation/FlightFilterBar/FightFilters';
import TourTypeFilter from '../TravelOption/TourInformation/TourFilters/TourTypeFilter';
import TourActivityFilter from '../TravelOption/TourInformation/TourFilters/TourActivityFilter';
import TourDurationFilter from '../TravelOption/TourInformation/TourFilters/TourDurationFilter';
import TourTimeWindowFilter from '../TravelOption/TourInformation/TourFilters/TourTimeWindowFilter';
import TourPriceFilter from '../TravelOption/TourInformation/TourFilters/TourPriceFilter';
import TourFeaturesFilter from '../TravelOption/TourInformation/TourFilters/TourFeaturesFilter';
import TourDifficultyFilter from '../TravelOption/TourInformation/TourFilters/TourDifficultyFilter';
import TourLanguageFilter from '../TravelOption/TourInformation/TourFilters/TourLanguageFilter';
import HotelNameFilter from '../TravelOption/HotelInformation/HotelFilterBar/HotelNameFilter';
import HotelChainsFilter from '../TravelOption/HotelInformation/HotelFilterBar/HotelChainsFilter';
import HotelPriceFilter from '../TravelOption/HotelInformation/HotelFilterBar/HotelPriceFilter';
import HotelAmenitiesFilter from '../TravelOption/HotelInformation/HotelFilterBar/HotelAmenitiesFilter';
import LoadMoreFlightsButton from '../TravelOption/FlightInformation/FlightPagination/LoadMoreFlightsButton';
import LoadMoreToursButton from '../TravelOption/TourInformation/LoadMoreToursButton';
import RateCodeKeys from '../TravelOption/HotelInformation/HotelFilterBar/RateCodeKey';
import SelectCurrency from '../SelectCurrency';
import FilterBar from './FilterBar';

const TravelOptionsPagination = ({ doShowSort }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { searchResult, selectedSearchMode } = useSelector((state) => state.tripSearch);
  const doShowMapButton = useSelector(checkDoHaveMapMarkers);
  const { travelAgency } = useSelector((store) => store.user);
  const { hotelPins } = useSelector((store) => store.tripSearch.searchResult);
  const doHidePostSearchFilters =
    selectedSearchMode === SearchModes.FLIGHT &&
    travelAgency &&
    travelAgency.settings &&
    travelAgency.settings.doHidePostSearchFilters;
  const {
    travelOptions,
    total: totalCount,
    foundTotalHotels,
    doHaveResultsWithoutFilter,
    doHideSabreFilters,
  } = searchResult || {};
  const { page, pageSize, sortBy, sortDirection, flightSearchType } = useSelector((store) => {
    if (selectedSearchMode === SearchModes.HOTEL) return store.tripSearch.searchObj.hotel;
    if (selectedSearchMode === SearchModes.FLIGHT) return store.tripSearch.searchObj.flight;
    if (selectedSearchMode === SearchModes.CAR) return store.tripSearch.searchObj.car;
    if (selectedSearchMode === SearchModes.TOUR) return store.tripSearch.searchObj.tour;
    return store.tripSearch.searchObj.cruise;
  });
  const doShowSortOptions = doShowSort;
  const doAlignCurrencyFilterWithOtherFilters = !!(
    travelAgency &&
    travelAgency.filterSettings &&
    travelAgency.filterSettings.doAlignCurrencyFilterWithOtherFilters
  );
  const { pageName } = router.query;
  const isChangeCruisePage =
    pageName === PageLinks.ChangeCruise.pageName || pageName === PageLinks.AddCruise.pageName;

  let SortingValues = CruiseSortValues;
  let InitialSortValue = FiltersInitialValue.cruise.sortBy;
  const doShowChatbotOnHotelSearchResults =
    travelAgency?.settings?.doShowChatbotOnHotelSearchResults;
  if (selectedSearchMode === SearchModes.HOTEL) {
    SortingValues = HotelSortingValues.filter(
      (h) => h.value !== HotelSortingType.DISTANCE_TO_CRUISE_PORT,
    );
    InitialSortValue = FiltersInitialValue.hotel.sortBy;
  }
  if (selectedSearchMode === SearchModes.CAR) {
    SortingValues = CarRentalSortingValues;
    InitialSortValue = FiltersInitialValue.car.sortBy;
  }
  if (selectedSearchMode === SearchModes.FLIGHT) {
    SortingValues =
      flightSearchType === FlightSearchTypes.ONE_WAY
        ? FlightSortingValues.filter((fs) => fs.value !== FlightSortingType.INBOUND_DURATION)
        : FlightSortingValues;
    InitialSortValue = FiltersInitialValue.flight.sortBy;
  }
  if (selectedSearchMode === SearchModes.CUSTOM) {
    SortingValues = CustomItinerarySortingValues;
    InitialSortValue = CustomItinerarySortingType.PRICE;
  }
  if (selectedSearchMode === SearchModes.TOUR) {
    SortingValues = TourSortingValues;
    InitialSortValue = TourFiltersInitialValue.sortBy;
  }

  const search = useCallback(
    (obj) => {
      const o = obj;
      if (!o.page || o.page < 1) o.page = 1;
      if (!selectedSearchMode || selectedSearchMode === SearchModes.CRUISE_TRIP) {
        dispatch(setCruiseSearchParam(o));
        dispatch(cruiseSearchRequest({ isChangeCruisePage }));
      } else if (selectedSearchMode === SearchModes.HOTEL) {
        dispatch(setIsUserInteractedWithHotelList(true));
        dispatch(setHotelTripSearchParam(o));
        dispatch(hotelSearchRequest());
      } else if (selectedSearchMode === SearchModes.FLIGHT) {
        dispatch(setFlightTripSearchParam(o));
        dispatch(flightSearch({ isTripSearch: true }));
      } else if (selectedSearchMode === SearchModes.CAR) {
        dispatch(setCarTripSearchParam(o));
        dispatch(carSearch({ isTripSearch: true, searchStatus: SearchStatus.SEARCHING }));
      } else if (selectedSearchMode === SearchModes.TOUR) {
        dispatch(setTourTripSearchParam(o));
        dispatch(tourSearchRequest({ isTripSearch: true }));
      }
      // TODO: load search obj from params
      // router.push(`/?o=${JSON.stringify(o)}`);
    },
    [dispatch, isChangeCruisePage, selectedSearchMode],
  );

  const handleNextPageClick = () => {
    const limit =
      page * pageSize > totalCount
        ? (page - 1) * pageSize + (totalCount % pageSize)
        : page * pageSize;
    const p = limit >= totalCount ? page : page + 1;
    if (p > page) {
      search({ page: p });
    }
  };

  const handlePrevPageClick = () => {
    const p = page - 1 > 0 ? page - 1 : page;

    if (p < page) {
      search({
        page: p,
      });
    }
  };

  const doHaveResults = travelOptions && totalCount > 0;

  const loadMoreButton = () => {
    if (selectedSearchMode === SearchModes.HOTEL) {
      return <LoadMoreHotelsButton totalHotels={totalCount} foundTotalHotels={foundTotalHotels} />;
    }
    if (selectedSearchMode === SearchModes.FLIGHT) {
      return (
        <LoadMoreFlightsButton totalFlights={totalCount} foundTotalFlights={foundTotalHotels} />
      );
    }
    if (selectedSearchMode === SearchModes.TOUR) {
      return <LoadMoreToursButton />;
    }
    return <></>;
  };

  const filterComponents = useMemo(
    () =>
      [
        {
          key: 'ratecode',
          element:
            doShowSort && !doHideSabreFilters ? (
              <RateCodeKeys
                filterSpacingClass="py-2 px-3"
                icon={
                  <FaRegQuestionCircle
                    className="text-green-600 min-w-4 place-self-center mr-1"
                    size={20}
                  />
                }
                selectedSearchMode={selectedSearchMode}
                fontWeight="font-normal"
              />
            ) : null,
        },
        {
          key: 'showMap',
          element: doShowMapButton && doShowSort ? <ShowHideTripItinerariesMap /> : null,
        },
        {
          key: 'pricePref',
          element:
            selectedSearchMode === SearchModes.HOTEL && doShowSort ? (
              <PricePreferenceFilter />
            ) : null,
        },
        {
          key: 'compare',
          element: doShowSort ? <CompareActionsDropdown /> : null,
        },
        {
          key: 'brochure',
          element: doShowSort ? <BrochureActions variant="transparentOutline" /> : null,
        },
        {
          key: 'sorting',
          element:
            !(flightSearchType === FlightSearchTypes.DIRECT_SELL) &&
            doShowSortOptions &&
            (doHaveResults || doHaveResultsWithoutFilter) &&
            SortingValues &&
            SortingValues.length > 0 ? (
              <SortingFilter
                sortingValues={SortingValues}
                initialValue={InitialSortValue}
                value={sortBy}
                direction={sortDirection}
                onSortChange={search}
                resultsLabel={SearchModeLabel[selectedSearchMode].toLowerCase()}
                variant="outline"
                filterSpacingClass="py-2 px-3"
                fontSize="text-base"
                dropdownAligning={selectedSearchMode === SearchModes.CAR ? 'left' : null}
              />
            ) : null,
        },
        {
          key: 'hotelName',
          element:
            doShowSortOptions && (doHaveResults || doHaveResultsWithoutFilter) ? (
              <HotelNameFilter variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'hotelAmenities',
          element:
            doShowSortOptions && (doHaveResults || doHaveResultsWithoutFilter) ? (
              <HotelAmenitiesFilter variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'hotelChains',
          element:
            selectedSearchMode === SearchModes.HOTEL &&
            doShowSortOptions &&
            (doHaveResults || doHaveResultsWithoutFilter) ? (
              <HotelChainsFilter variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'hotelPrice',
          element:
            selectedSearchMode === SearchModes.HOTEL &&
            doShowSortOptions &&
            (doHaveResults || doHaveResultsWithoutFilter) ? (
              <HotelPriceFilter variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'currency',
          element:
            doShowSortOptions &&
            (doHaveResults || doHaveResultsWithoutFilter) &&
            doAlignCurrencyFilterWithOtherFilters ? (
              <SelectCurrency variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'tourType',
          element:
            selectedSearchMode === SearchModes.TOUR &&
            doShowSort &&
            (doHaveResults || doHaveResultsWithoutFilter) ? (
              <TourTypeFilter variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'tourActivity',
          element:
            selectedSearchMode === SearchModes.TOUR &&
            doShowSort &&
            (doHaveResults || doHaveResultsWithoutFilter) ? (
              <TourActivityFilter variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'tourDuration',
          element:
            selectedSearchMode === SearchModes.TOUR &&
            doShowSort &&
            (doHaveResults || doHaveResultsWithoutFilter) ? (
              <TourDurationFilter variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'tourTimeWindow',
          element:
            selectedSearchMode === SearchModes.TOUR &&
            doShowSort &&
            (doHaveResults || doHaveResultsWithoutFilter) ? (
              <TourTimeWindowFilter variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'tourPrice',
          element:
            selectedSearchMode === SearchModes.TOUR &&
            doShowSort &&
            (doHaveResults || doHaveResultsWithoutFilter) ? (
              <TourPriceFilter variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'tourFeatures',
          element:
            selectedSearchMode === SearchModes.TOUR &&
            doShowSort &&
            (doHaveResults || doHaveResultsWithoutFilter) ? (
              <TourFeaturesFilter variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'tourDifficulty',
          element:
            selectedSearchMode === SearchModes.TOUR &&
            doShowSort &&
            (doHaveResults || doHaveResultsWithoutFilter) ? (
              <TourDifficultyFilter variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'tourLanguage',
          element:
            selectedSearchMode === SearchModes.TOUR &&
            doShowSort &&
            (doHaveResults || doHaveResultsWithoutFilter) ? (
              <TourLanguageFilter variant="outline" filterSpacingClass="py-2 px-3" />
            ) : null,
        },
        {
          key: 'flightFilters',
          element:
            selectedSearchMode === SearchModes.FLIGHT && doShowSort && !doHidePostSearchFilters ? (
              <FightFilters />
            ) : null,
        },
      ].filter((f) => f.element),
    [
      InitialSortValue,
      SortingValues,
      doAlignCurrencyFilterWithOtherFilters,
      doHaveResults,
      doHaveResultsWithoutFilter,
      doHidePostSearchFilters,
      doHideSabreFilters,
      doShowMapButton,
      doShowSort,
      doShowSortOptions,
      flightSearchType,
      search,
      selectedSearchMode,
      sortBy,
      sortDirection,
    ],
  ); // Remove nulls

  return (
    <>
      {(doHaveResults || doHaveResultsWithoutFilter) && (
        <>
          {loadMoreButton()}
          <div className="sm:flex gap-1">
            <FilterBar
              key={selectedSearchMode}
              filterComponents={filterComponents}
              hasAdjacentContent={!!(doHaveResults || doHaveResultsWithoutFilter)}
              withBottomMargin={
                !(
                  selectedSearchMode === SearchModes.HOTEL &&
                  doShowChatbotOnHotelSearchResults &&
                  hotelPins?.length > 0
                )
              }
            />
            {(doHaveResults || doHaveResultsWithoutFilter) && (
              <Pagination
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                onPrevPageClick={handlePrevPageClick}
                onNextPageClick={handleNextPageClick}
                resultsLabel={SearchModeLabel[selectedSearchMode].toLowerCase()}
                searchMode={selectedSearchMode}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

TravelOptionsPagination.propTypes = {
  doShowSort: PropTypes.bool,
};

TravelOptionsPagination.defaultProps = {
  doShowSort: true,
};

export default memo(TravelOptionsPagination);
